import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import {
  useTripperScrollContext,
  useTripperScrollProgressContext,
  useTripperWidgetContext,
  useVerticalStack,
} from "../context/TripperWidgetContext";
import { hasProgressChanged } from "../utils/scrollHelpers";

// ── Pure helpers (exported for testing) ──────────────────────────────────────

export type ScrollSyncResult = {
  activeItemId: string | null;
  activeSegmentId: string | null;
  activeGroupId: string | null;
  segmentProgress: Record<string, number>;
  isMetadataInView: boolean;
};

/**
 * Pure function that reads DOM positions and computes which stop, segment, and
 * group are active.  Extracted from the hook so it can be unit-tested without
 * React or a real browser environment.
 */
export function computeScrollState(container: HTMLElement): ScrollSyncResult {
  const containerRect = container.getBoundingClientRect();
  const thresholdY = containerRect.top + containerRect.height * 0.5;

  // --- Segment detection + progress ---
  const segmentProgress: Record<string, number> = {};
  const sections = Array.from(
    container.querySelectorAll<HTMLElement>(".tripper-section[data-segment-id]"),
  );

  let activeSegmentId: string | null = null;
  for (const sectionEl of sections) {
    const segId = sectionEl.dataset.segmentId;
    if (!segId) continue;

    const rect = sectionEl.getBoundingClientRect();
    const scrolled = thresholdY - rect.top;
    const height = rect.height;

    segmentProgress[segId] =
      height <= 0 ? 0 : Math.max(0, Math.min(1, scrolled / height));

    if (rect.top <= thresholdY) {
      activeSegmentId = segId;
    }
  }

  // --- Group detection ---
  const groups = Array.from(
    container.querySelectorAll<HTMLElement>(".tripper-group[data-group-id]"),
  );

  let activeGroupId: string | null = null;
  for (const groupEl of groups) {
    const grpId = groupEl.dataset.groupId;
    if (!grpId) continue;
    const rect = groupEl.getBoundingClientRect();
    if (rect.top <= thresholdY && rect.bottom > thresholdY) {
      activeGroupId = grpId;
      break;
    }
  }

  // --- Stop detection ---
  const items = Array.from(
    container.querySelectorAll<HTMLElement>(".tripper-item[data-item-id]"),
  );

  let activeItemId: string | null = null;
  let closestDistance = Infinity;

  for (const item of items) {
    const id = item.dataset.itemId;
    if (!id) continue;
    const rect = item.getBoundingClientRect();
    // Use a point offset into the element (proportional to height, capped)
    // so short stops "reach" the threshold sooner while tall ones barely change.
    const offset = Math.min(rect.height * 0.4, containerRect.height * 0.15);
    const activationY = rect.top + offset;
    if (activationY <= thresholdY) {
      activeItemId = id;
    } else if (!activeItemId) {
      const distance = activationY - thresholdY;
      if (distance < closestDistance) {
        closestDistance = distance;
        activeItemId = id;
      }
    }
  }

  // --- Metadata ---
  let isMetadataInView = true;
  const metadataEl = container.querySelector<HTMLElement>(".tripper-metadata");
  if (metadataEl) {
    const metadataRect = metadataEl.getBoundingClientRect();
    const scrolledPast = Math.max(0, containerRect.top - metadataRect.top);
    isMetadataInView = scrolledPast < metadataRect.height * 0.4;
  }

  return { activeItemId, activeSegmentId, activeGroupId, segmentProgress, isMetadataInView };
}

/** Read segment/group context for a content item element. */
export function readScrollItemContext(target: HTMLElement): {
  itemId: string | null;
  segmentId: string | null;
  groupId: string | null;
} {
  return {
    itemId: target.dataset.itemId ?? null,
    segmentId:
      target.closest<HTMLElement>(".tripper-section[data-segment-id]")?.dataset
        .segmentId ?? null,
    groupId:
      target.closest<HTMLElement>(".tripper-group[data-group-id]")?.dataset
        .groupId ?? null,
  };
}

const ACTIVATION_MARGIN_PX = 8;

export type ScrollSyncAlignment = "center" | "top";

const DEFAULT_TOP_SCROLL_MARGIN_PX = 32;

/** Scroll delta needed to place the item at the sync threshold (center or top). */
export function computeScrollDeltaToActiveThreshold(
  container: HTMLElement,
  target: HTMLElement,
  margin = ACTIVATION_MARGIN_PX,
  alignment: ScrollSyncAlignment = "center",
  topMargin = DEFAULT_TOP_SCROLL_MARGIN_PX,
): number {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  if (alignment === "top") {
    const thresholdY = containerRect.top + topMargin;
    return targetRect.top - thresholdY;
  }

  const thresholdY = containerRect.top + containerRect.height * 0.5;
  const offset = Math.min(targetRect.height * 0.4, containerRect.height * 0.15);
  const activationY = targetRect.top + offset;
  return activationY - thresholdY + margin;
}

/**
 * Scroll so the item aligns with computeScrollState's threshold (center) or the top inset.
 * Keeps the tapped stop selected after the scroll settles (avoids off-by-one).
 */
export function scrollItemToActiveThreshold(
  container: HTMLElement,
  target: HTMLElement,
  behavior: ScrollBehavior = "smooth",
  options?: {
    margin?: number;
    alignment?: ScrollSyncAlignment;
    topMargin?: number;
  },
): void {
  const alignment = options?.alignment ?? "center";
  const scrollDelta = computeScrollDeltaToActiveThreshold(
    container,
    target,
    options?.margin ?? ACTIVATION_MARGIN_PX,
    alignment,
    options?.topMargin ?? DEFAULT_TOP_SCROLL_MARGIN_PX,
  );
  if (Math.abs(scrollDelta) < 0.5) return;

  container.scrollTo({
    top: container.scrollTop + scrollDelta,
    behavior,
  });
}

// ── Hook ─────────────────────────────────────────────────────────────────────

type UseScrollSyncProps = {
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** Changing this value forces the scroll listener to re-attach (e.g. after a view switch). */
  resetKey?: string;
};

export const useScrollSync = ({
  scrollContainerRef,
  resetKey,
}: UseScrollSyncProps) => {
  const {
    setActiveItemId,
    setActiveSegmentId,
    setActiveStopGroupId,
    setIsMetadataInView,
  } = useTripperScrollContext();
  const { setSegmentScrollProgress } = useTripperScrollProgressContext();
  const { setIsAutoFollowActive } = useTripperWidgetContext();
  const verticalStack = useVerticalStack();

  const markerScrollAlign = useMemo(
    () =>
      verticalStack
        ? ({ alignment: "top" as const, topMargin: DEFAULT_TOP_SCROLL_MARGIN_PX })
        : ({ alignment: "center" as const }),
    [verticalStack],
  );

  const lastActiveRef = useRef<string | null>(null);
  const lastSegmentRef = useRef<string | null>(null);
  const lastGroupRef = useRef<string | null>(null);
  const lastProgressRef = useRef<Record<string, number>>({});
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgrammaticScroll = useRef(false);
  const programmaticTimer = useRef<ReturnType<typeof setTimeout>>();
  const programmaticTargetIdRef = useRef<string | null>(null);

  const applyScrollState = useCallback(
    (state: ScrollSyncResult) => {
      if (state.activeSegmentId !== lastSegmentRef.current) {
        lastSegmentRef.current = state.activeSegmentId;
        setActiveSegmentId(state.activeSegmentId);
      }

      if (hasProgressChanged(state.segmentProgress, lastProgressRef.current)) {
        lastProgressRef.current = state.segmentProgress;
        setSegmentScrollProgress(state.segmentProgress);
      }

      if (state.activeGroupId !== lastGroupRef.current) {
        lastGroupRef.current = state.activeGroupId;
        setActiveStopGroupId(state.activeGroupId);
      }

      if (state.activeItemId !== lastActiveRef.current) {
        lastActiveRef.current = state.activeItemId;
        setActiveItemId(state.activeItemId);
      }

      setIsMetadataInView(state.isMetadataInView);
    },
    [
      setActiveItemId,
      setActiveSegmentId,
      setActiveStopGroupId,
      setIsMetadataInView,
      setSegmentScrollProgress,
    ],
  );

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    applyScrollState(computeScrollState(container));
  }, [scrollContainerRef, applyScrollState]);

  const finishProgrammaticScroll = useCallback(() => {
    if (!isProgrammaticScroll.current) return;
    isProgrammaticScroll.current = false;
    clearTimeout(programmaticTimer.current);

    const targetId = programmaticTargetIdRef.current;
    programmaticTargetIdRef.current = null;

    const container = scrollContainerRef.current;
    if (!container) return;

    if (targetId) {
      const target = container.querySelector<HTMLElement>(
        `.tripper-item[data-item-id="${targetId}"]`,
      );
      if (target) {
        // Smooth scroll often undershoots; snap precisely before re-syncing.
        scrollItemToActiveThreshold(container, target, "instant", markerScrollAlign);
      }
    }

    const state = computeScrollState(container);
    if (targetId) {
      state.activeItemId = targetId;
      lastActiveRef.current = targetId;
    }
    applyScrollState(state);
  }, [scrollContainerRef, applyScrollState, markerScrollAlign]);

  const scheduleProgrammaticFinish = useCallback(() => {
    clearTimeout(programmaticTimer.current);
    programmaticTimer.current = setTimeout(finishProgrammaticScroll, 150);
  }, [finishProgrammaticScroll]);

  const activateItemFromElement = useCallback(
    (target: HTMLElement) => {
      const { itemId, segmentId, groupId } = readScrollItemContext(target);
      if (!itemId) return;

      lastActiveRef.current = itemId;
      lastSegmentRef.current = segmentId;
      lastGroupRef.current = groupId;
      setActiveItemId(itemId);
      setActiveSegmentId(segmentId);
      setActiveStopGroupId(groupId);
      setIsMetadataInView(false);
    },
    [
      setActiveItemId,
      setActiveSegmentId,
      setActiveStopGroupId,
      setIsMetadataInView,
    ],
  );

  const scrollToItem = useCallback(
    (itemId: string) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const target = container.querySelector<HTMLElement>(
        `.tripper-item[data-item-id="${itemId}"]`,
      );
      if (!target) return;

      programmaticTargetIdRef.current = itemId;
      isProgrammaticScroll.current = true;
      clearTimeout(programmaticTimer.current);
      setIsAutoFollowActive(true);
      activateItemFromElement(target);

      const scrollDelta = computeScrollDeltaToActiveThreshold(
        container,
        target,
        ACTIVATION_MARGIN_PX,
        markerScrollAlign.alignment,
        markerScrollAlign.alignment === "top"
          ? markerScrollAlign.topMargin
          : DEFAULT_TOP_SCROLL_MARGIN_PX,
      );

      if (Math.abs(scrollDelta) < 0.5) {
        finishProgrammaticScroll();
        return;
      }

      scrollItemToActiveThreshold(container, target, "smooth", markerScrollAlign);
      // Safety fallback if scroll events stop firing before the animation ends.
      programmaticTimer.current = setTimeout(finishProgrammaticScroll, 3000);
    },
    [
      scrollContainerRef,
      setIsAutoFollowActive,
      activateItemFromElement,
      finishProgrammaticScroll,
      markerScrollAlign,
    ],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const throttledUpdate = () => {
      if (isProgrammaticScroll.current) {
        scheduleProgrammaticFinish();
        return;
      }

      if (throttleRef.current) return;
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null;
        updateScrollState();
      }, 80);
    };

    updateScrollState();

    container.addEventListener("scroll", throttledUpdate, { passive: true });
    window.addEventListener("resize", throttledUpdate);
    return () => {
      container.removeEventListener("scroll", throttledUpdate);
      window.removeEventListener("resize", throttledUpdate);
      if (throttleRef.current) clearTimeout(throttleRef.current);
      clearTimeout(programmaticTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resetKey intentionally forces re-attach
  }, [scrollContainerRef, updateScrollState, scheduleProgrammaticFinish, resetKey]);

  return { scrollToItem };
};
